import '@material/web/button/outlined-button.js';
import { getLang, setLang, t } from './i18n.js';
import { registerTool, listTools, getTool } from './tools/registry.js';
import { friendLinks } from './links.js';
import { navigate } from './core/navigate.js';
import { HOME_CONTENT, TOOL_PAGES, renderBlocks } from './seo-content.js';
import { mountFeedback } from './feedback.js';

// Register the tools. Each loader is a dynamic import so a tool's heavy
// dependencies (AI models, zip libs) are only fetched when the tool is opened.
registerTool({
  id: 'frames',
  titleKey: 'tool.frames.title',
  descKey: 'tool.frames.desc',
  iconTextKey: 'tool.frames.iconText',
  loader: () => import('./tools/frames/index.js'),
});
registerTool({
  id: 'bg-remove',
  titleKey: 'tool.bg.title',
  descKey: 'tool.bg.desc',
  iconTextKey: 'tool.bg.iconText',
  loader: () => import('./tools/bg-remove/index.js'),
});
registerTool({
  id: 'sprite-sheet',
  titleKey: 'tool.spriteSheet.title',
  descKey: 'tool.spriteSheet.desc',
  iconTextKey: 'tool.spriteSheet.iconText',
  loader: () => import('./tools/sprite-sheet/index.js'),
});
registerTool({
  id: 'compress',
  titleKey: 'tool.compress.title',
  descKey: 'tool.compress.desc',
  iconTextKey: 'tool.compress.iconText',
  loader: () => import('./tools/compress/index.js'),
});

const view = document.getElementById('view');

// Tracks which view is currently shown: 'home' or a tool id. Used to know
// whether the home grid needs re-rendering when the language changes.
let currentView = 'home';

function renderHome() {
  currentView = 'home';
  const tools = listTools();
  view.innerHTML = `
    <section class="home">
      <h2 data-i18n="home.title">${t('home.title')}</h2>
      <p class="home-sub" data-i18n="home.subtitle">${t('home.subtitle')}</p>
      <div class="tool-grid">
        ${tools
          .map(
            (tool) => `
          <a class="tool-card" href="/${tool.id}">
            <span class="tool-icon">${t(tool.iconTextKey)}</span>
            <div class="tool-card-body">
              <h3>${t(tool.titleKey)}</h3>
              <p>${t(tool.descKey)}</p>
            </div>
            <span class="tool-go" aria-hidden="true">→</span>
          </a>`,
          )
          .join('')}
      </div>
    </section>`;
}

// Build the sidebar nav once. Labels and icons carry data-i18n so
// applyLanguage() refreshes their text without re-rendering, which keeps the
// active highlight intact across language switches.
function renderSidebar() {
  const nav = document.getElementById('sidebar-nav');
  const tools = listTools();
  nav.innerHTML = `
    <li><a class="nav-link" href="/" data-route="">
      <span class="nav-icon">⌂</span>
      <span class="nav-label" data-i18n="nav.home">${t('nav.home')}</span>
    </a></li>
    ${tools
      .map(
        (tool) => `
      <li><a class="nav-link" href="/${tool.id}" data-route="${tool.id}">
        <span class="nav-icon" data-i18n="${tool.iconTextKey}">${t(tool.iconTextKey)}</span>
        <span class="nav-label" data-i18n="${tool.titleKey}">${t(tool.titleKey)}</span>
      </a></li>`,
      )
      .join('')}`;
}

// Render the footer friend links. Re-run on language change so the link
// names/descriptions are re-translated.
function renderFriendLinks() {
  const list = document.getElementById('friend-links-list');
  list.innerHTML = friendLinks
    .map(
      (link) => `
      <li>
        <a href="${link.url}" target="_blank" rel="noopener">
          <span class="friend-link-name">${t(link.nameKey)}</span>
          <span class="friend-link-desc">${t(link.descKey)}</span>
        </a>
      </li>`,
    )
    .join('');
}

// Highlight the nav entry matching the current route ('' = home).
function updateActiveNav(id) {
  for (const link of document.querySelectorAll('#sidebar-nav .nav-link')) {
    const active = link.dataset.route === id;
    link.classList.toggle('active', active);
    if (active) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  }
}

let currentCleanup = null;
// The TOOL_PAGES entry for the currently shown tool, or null on the home page.
// Used to drive the per-page <title>, meta description, and SEO content.
let currentPage = null;

function pageTitle(page) {
  return page ? page.title[getLang()] : t('app.title');
}

function pageDescription(page) {
  return page ? page.description[getLang()] : t('seo.description');
}

// Re-render the SEO content section for the current page in the current
// language. The static HTML ships the Chinese default for crawlers; this keeps
// it in sync when the user switches language or navigates between tools.
function renderSeoContent() {
  const section = document.querySelector('.seo-content');
  if (!section) return;
  const blocks = currentPage ? currentPage.blocks : HOME_CONTENT;
  section.innerHTML = renderBlocks(blocks, getLang());
}

function applyLanguage() {
  document.documentElement.lang = getLang() === 'zh' ? 'zh-CN' : 'en';
  feedback.setLang(getLang());
  document.title = pageTitle(currentPage);
  document.querySelector('meta[name="description"]').setAttribute('content', pageDescription(currentPage));
  document.querySelector('meta[property="og:title"]').setAttribute('content', pageTitle(currentPage));
  document.querySelector('meta[property="og:description"]').setAttribute('content', pageDescription(currentPage));
  document.querySelector('meta[name="twitter:title"]').setAttribute('content', pageTitle(currentPage));
  document.querySelector('meta[name="twitter:description"]').setAttribute('content', pageDescription(currentPage));
  for (const el of document.querySelectorAll('[data-i18n]')) {
    el.textContent = t(el.dataset.i18n);
  }
  for (const el of document.querySelectorAll('[data-i18n-label]')) {
    el.label = t(el.dataset.i18nLabel);
  }
  document.getElementById('lang-toggle').textContent = t('common.lang');
  // The home grid bakes translated text into the cards at render time, so
  // re-render it on a language change to refresh the card titles/descriptions.
  if (currentView === 'home') {
    renderHome();
  }
  renderSeoContent();
  renderFriendLinks();
  window.dispatchEvent(new CustomEvent('app:lang'));
}

// Map a pathname to a tool id ('' for the home page). Tolerates a trailing
// slash, e.g. "/frames/" -> "frames".
function pathToToolId(pathname) {
  const clean = pathname.replace(/\/+$/, '');
  if (clean === '' || clean === '/') return '';
  const id = clean.replace(/^\//, '');
  return getTool(id) ? id : '';
}

async function route() {
  const id = pathToToolId(location.pathname);
  if (currentCleanup) {
    try {
      currentCleanup();
    } catch {
      /* ignore cleanup errors */
    }
    currentCleanup = null;
  }

  const tool = getTool(id);
  updateActiveNav(id);
  if (tool) {
    currentView = tool.id;
    currentPage = TOOL_PAGES.find((p) => p.id === tool.id) || null;
    view.innerHTML = '';
    view.classList.add('tool-view');
    const mod = await tool.loader();
    currentCleanup = mod.mount(view) || null;
    applyLanguage();
  } else {
    currentView = 'home';
    currentPage = null;
    view.classList.remove('tool-view');
    renderHome();
    applyLanguage();
  }
}

// Intercept same-origin internal links (e.g. "/frames", "/") so navigation
// happens in-page via pushState. A full page reload would wipe the in-memory
// transfer store that carries frames/images between tools.
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[href]');
  if (!link) return;
  const href = link.getAttribute('href');
  if (!href || !href.startsWith('/')) return;
  e.preventDefault();
  navigate(href);
});

document.getElementById('lang-toggle').addEventListener('click', () => {
  setLang(getLang() === 'zh' ? 'en' : 'zh');
  applyLanguage();
});

window.addEventListener('popstate', route);

// Redirect legacy hash routes (e.g. #/frames) to the path-based URL so old
// links keep working.
const legacyHash = location.hash.replace(/^#\/?/, '');
if (legacyHash && getTool(legacyHash)) {
  history.replaceState(null, '', `/${legacyHash}`);
}

// Floating feedback entry (shared widget; posts to /api/feedback).
const feedback = mountFeedback({ site: 'assetkit', email: 'hi@assetkit.top' });

renderSidebar();
route();
applyLanguage();
